import { Button, Form, Modal, Space, theme } from "antd"
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { unstable_useBlocker as useBlocker, useLocation } from "react-router-dom"
import styled from "styled-components"

const AdvancedFormContext = createContext({ readOnly: false })

const FormStyled = styled(Form)`
  max-height: 100%;
  display: flex;
  flex-direction: column;
`

export const useAdvancedFormContext = () => useContext(AdvancedFormContext)

interface Blocker {
  state: "unblocked" | "blocked" | "proceeding";
  proceed?: () => void,
  reset?: () => void,
}

const useEditingWithBlocker = (blocked: boolean, initialEditing: boolean) => {
  const [editing, setEditing] = useState<boolean>(initialEditing)
  const [blockedTriggered, setBlockedTriggered] = useState<boolean>(false)

  const setEditingInternal = useCallback((newEditing: boolean | ((arg: boolean) => boolean)) => {
    console.log("Triggering set editing internal");
    let curEditing = false;
    setEditing(v => { curEditing = v; return v; })
    newEditing = typeof newEditing === 'boolean' ? newEditing : newEditing(curEditing)
    if(!newEditing && blocked) {
      setBlockedTriggered(true);
    } else {
      //console.log("Setting editing", newEditing)
      setEditing(newEditing);
    }
  }, [blocked, setBlockedTriggered, setEditing])

  const blocker: Blocker = useMemo(() => ({
    state: blockedTriggered ? "blocked" : "unblocked",
    proceed: blockedTriggered ? () => { setBlockedTriggered(false); setEditing(false) } : undefined,
    reset: blockedTriggered ? () => setBlockedTriggered(false) : undefined,
  }), [blockedTriggered, setEditing, setBlockedTriggered])

  return { editing, setEditing: setEditingInternal, setEditingWithoutCheck: setEditing, blocker }
}

const useCombinedBlockers = (...blockers: Blocker[]) => {
  const state = blockers.map(b => b.state).reduce((p, c) => c === 'blocked' ? 'blocked' : p, 'unblocked');
  const proceed = blockers.map(b => b.proceed).reduce((p, c) => c ?? p, undefined);
  const reset = blockers.map(b => b.reset).reduce((p, c) => c ?? p, undefined);

  return { state, proceed, reset }
}

export const AdvancedForm = ({children, form, onFinish, originalValues, isLoading, initialEditing, ...rest}: any) => {
  const [formChanged, setFormChanged] = useState<boolean>(false);
  const { editing, setEditing, setEditingWithoutCheck, blocker: editingBlocker } = useEditingWithBlocker(formChanged, initialEditing);
  const navigationBlocker: Blocker = useBlocker(formChanged);
  const blocker = useCombinedBlockers(navigationBlocker, editingBlocker);
  const location = useLocation();
  const lastLocationRef = useRef<any>(location);

  useEffect(() => {
    console.log("Original values changed: ", originalValues)
    //form.setFieldsValue(originalValues)
    form.resetFields();
    setFormChanged(false);
  }, [form, originalValues, setFormChanged])

  useEffect(() => {
    if(location !== lastLocationRef.current) {
      setEditingWithoutCheck(false);
      lastLocationRef.current = location;
    }
  }, [setEditingWithoutCheck, location])

  const proceedWithoutSave = useCallback(() => {
    //form.setFieldsValue(originalValues);
    form.resetFields();
    setFormChanged(false);
    blocker.proceed && blocker.proceed();
  }, [form, blocker, setFormChanged])

  const onFinishInternal = useCallback(async (...args) => {
    setFormChanged(false);
    await new Promise(resolve => setTimeout(resolve, 0));
    await onFinish(...args);
  }, [onFinish, setFormChanged])

  const proceedWithSave = useCallback(async () => {
    const values = form.getFieldsValue();
    await onFinishInternal(values);
    blocker.proceed && blocker.proceed();
  }, [form, onFinishInternal, blocker])

  const { token } = theme.useToken();

  const formStyle: React.CSSProperties = {
    maxWidth: 'none',
    background: token.colorFillAlter,
    borderRadius: token.borderRadiusLG,
    padding: 24,
  };

  return (
    <>
      <Modal width={550} 
          title="Ungespeicherte Änderungen" 
          open={blocker.state === 'blocked'} 
          footer={<Space><Button onClick={blocker.reset}>Abbrechen</Button><Button onClick={proceedWithoutSave}>Änderungen verwerfen &amp; weiter</Button><Button type="primary" onClick={proceedWithSave}>Speichern &amp; weiter</Button></Space>} 
          onCancel={blocker.reset}
          >
        <p>Sie haben noch ungespeicherte Änderungen im Formular.</p>
        <p>Wollen Sie speichern und dann fortfahren?</p>
      </Modal>
      <AdvancedFormContext.Provider value={{ readOnly: !editing }}>
        <FormStyled form={form} onFinish={onFinishInternal} onValuesChange={() => setFormChanged(true)} style={formStyle} initialValues={originalValues} {...rest}>
          <div style={{ textAlign: 'right', paddingRight: "2em" }}>
            <Space size="small">
              <Button type="primary" onClick={() => setEditing(v => !v)}>
                { editing ? "Bearbeitung beenden" : "Bearbeiten" }
              </Button>
            </Space>
          </div>
          <div style={{ flex: 1, overflowY: "scroll", paddingRight: "2em" }}>
          { children}
          </div>
          <div style={{ textAlign: 'right', paddingRight: "2em" }}>
            <Space size="small">
              <Button disabled={!editing} loading={isLoading} type="primary" htmlType="submit">
                Speichern
              </Button>
            </Space>
          </div>
        </FormStyled>
      </AdvancedFormContext.Provider>
    </>
  )
}